import React, {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
    ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import useTranslationContext, { Language } from "Contexts/translation-context";
import useBrandContext from "Contexts/brand-context";
import useCustomerContext from "Contexts/customer-context";
import {
    NotificationBannerType,
    useNotificationBannersContext,
} from "Contexts/notification-banners-context";
import { stringInterpolate } from "Shared/util/string-helper";
import { setDisplayName, hideLauncher } from "Shared/util/imbox-api";
import useUserContext from "../../contexts/user-context";

export const useMaxWidth = (maxWidth: number) => {
    const [isValidWidth, setIsValidWidth] = useState(
        window.innerWidth <= maxWidth,
    );

    useEffect(() => {
        const handleResize = () =>
            setIsValidWidth(window.innerWidth <= maxWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isValidWidth;
};

export const useMobileMode = () => useMaxWidth(768);

export const useOnClickOutside = (ref: any, handler: any) => {
    useEffect(() => {
        const listener = (event: any) => {
            if (!ref) return;
            if (ref.contains && ref.contains(event.target)) return;
            if (
                ref.current &&
                ref.current.contains &&
                ref.current.contains(event.target)
            )
                return;

            handler(event);
        };

        document.addEventListener("mousedown", listener);

        return () => {
            document.removeEventListener("mousedown", listener);
        };
    }, [ref, handler]);
};

export const useHeaderHeight = () => {
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const calculateHeaderHeight = () => {
            const header = document.querySelector(".mainHeader");
            if (header && header.clientHeight !== headerHeight) {
                setHeaderHeight(header.clientHeight);
            }
        };

        calculateHeaderHeight();

        window.addEventListener("resize", calculateHeaderHeight);
        window.addEventListener("orientationchange", calculateHeaderHeight);

        return () => {
            window.removeEventListener("resize", calculateHeaderHeight);
            window.removeEventListener(
                "orientationchange",
                calculateHeaderHeight,
            );
        };
    }, [headerHeight]);

    return headerHeight;
};

export const useTopbarHeight = () => {
    const [topbarHeight, setTopbarHeight] = useState(0);

    useEffect(() => {
        const calculateTopBarHeight = () => {
            const topbar = document.querySelector(".topbarHeader");
            if (topbar && topbar.clientHeight !== topbarHeight) {
                setTopbarHeight(topbar.clientHeight);
            }
        };

        calculateTopBarHeight();

        window.addEventListener("resize", calculateTopBarHeight);

        return () => {
            window.removeEventListener("resize", calculateTopBarHeight);
        };
    }, [topbarHeight]);

    return topbarHeight;
};

export const useWindowHeight = () => {
    const [windowHeight, setWindowHeight] = useState(0);

    useEffect(() => {
        const calculateWindowHeight = () => {
            if (window.innerHeight !== windowHeight) {
                setWindowHeight(window.innerHeight);
            }
        };

        calculateWindowHeight();

        window.addEventListener("resize", calculateWindowHeight);
        window.addEventListener("orientationchange", calculateWindowHeight);

        return () => {
            window.removeEventListener("resize", calculateWindowHeight);
            window.removeEventListener(
                "orientationchange",
                calculateWindowHeight,
            );
        };
    }, [windowHeight]);

    return windowHeight;
};

export type StatusMessageType =
    | "FAILED"
    | "INFO"
    | "PENDING"
    | "SUCCEEDED"
    | "WARNING"
    | "NONE";

export type StatusMessage = {
    type: StatusMessageType;
    message?: any;
};

export const useStatusMessage = (): [
    StatusMessage,
    (action: StatusMessage) => void,
] => {
    const [statusMessage, setStatusMessage] = useState<StatusMessage>({
        type: "NONE",
        message: "",
    });
    return [statusMessage, setStatusMessage];
};

export const useImbox = () => {
    const { brandData, loadingBrandData } = useBrandContext();
    const { customerData, loadingCustomerData } = useCustomerContext();
    const { user } = useUserContext();

    const { fullName } = user || {};

    useEffect(() => {
        let script: any;
        if (!loadingBrandData) {
            const sid = brandData.imbox.widgetId;
            if (!!sid) {
                // eslint-disable-next-line no-underscore-dangle
                (window as any)._sid = sid;
                script = document.createElement("script");
                script.id = "imbox-extention";
                script.type = "text/javascript";
                script.async = true;
                script.src = `${document.location.protocol}//files.imbox.io/app/dist/initWidget.js`;
                document.body.appendChild(script);
            }
            // eslint-disable-next-line no-underscore-dangle
            (window as any)._imbox = (window as any)._imbox || [];
            hideLauncher();
        }
        return () => {
            if (document.getElementById("imbox-extention")) {
                document.body.removeChild(script);
            }
        };
    }, [loadingBrandData]);

    useEffect(() => {
        if (
            document.getElementById("imbox-extention") &&
            !loadingCustomerData
        ) {
            const { name, number } = customerData || {};
            setDisplayName(`${fullName} (${name}, ${number})`);
        }
    }, [loadingBrandData, loadingCustomerData]);
};

export const useModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return {
        isModalOpen,
        openModal,
        closeModal,
    };
};

export const useCombinedRefs = (ref: any) => {
    const innerRef = useRef();

    useEffect(() => {
        if (!ref) return;

        if (typeof ref === "function") {
            ref(innerRef.current);
        } else {
            ref.current = innerRef.current;
        }
    });

    return innerRef;
};

export const useQueryParams = (): URLSearchParams => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

export const usePrevious = (value: any) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

export const useInterval = (callback: any, delay?: number) => {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    });

    useEffect(() => {
        if (typeof delay !== "number") return undefined;

        const tick = () => {
            if (typeof savedCallback.current !== "undefined") {
                (savedCallback as any).current();
            }
        };

        const id = setInterval(tick, delay);
        return () => clearInterval(id);
    }, [delay]);
};

export const useGlobalNotificationBanner = () => {
    const tnb = useTranslation((x) => x.notification_banners);
    const { handleAddNotification, handleRemoveNotification } =
        useNotificationBannersContext();

    const addGlobalNotificationBanner = useCallback(
        (
            content: React.ReactNode | ((x: typeof tnb) => React.ReactNode),
            type = NotificationBannerType.Information,
            allowClose = false,
            id: string = "",
        ) => handleAddNotification(content, type, allowClose, true, id),
        [handleAddNotification],
    );

    const removeGlobalNotificationBanner = useCallback(
        (id: string) => handleRemoveNotification(id),
        [handleRemoveNotification],
    );

    return {
        NotificationBannerType: NotificationBannerType,
        addGlobalNotificationBanner,
        removeGlobalNotificationBanner,
    };
};

export const useLocalNotificationBanner = (
    removeNotificationsOnUnmount = true,
) => {
    const tnb = useTranslation((x) => x.notification_banners);
    const {
        handleAddNotification,
        handleRemoveNotification,
        handleRemoveLocalNotifications,
    } = useNotificationBannersContext();

    useEffect(() => {
        return () => {
            if (removeNotificationsOnUnmount) {
                handleRemoveLocalNotifications();
            }
        };
    }, [handleRemoveLocalNotifications, removeNotificationsOnUnmount]);

    const addLocalNotificationBanner = useCallback(
        (
            content: ReactNode | ((x: typeof tnb) => ReactNode),
            type = NotificationBannerType.Information,
            allowClose = true,
            id: string = "",
        ) => handleAddNotification(content, type, allowClose, false, id),
        [handleAddNotification],
    );

    return {
        NotificationBannerType: NotificationBannerType,
        addLocalNotificationBanner,
        removeLocalNotificationBanner: handleRemoveNotification,
        removeAllLocalNotificationBanners: handleRemoveLocalNotifications,
    };
};

type TranslationItem<T> = T & {
    findByKey: (key: string) => string;
    format: (template: string, values: any) => string;
};

export const useTranslation = <T>(
    selector: (x: Language) => T,
): TranslationItem<T> => {
    const { translationData } = useTranslationContext();
    const { translations } = translationData || {};

    return useMemo(() => {
        const componentData: T = translations
            ? selector(translations)
            : ({} as T);

        const findByKey = (key: string): string =>
            componentData[key as keyof T] as string;

        const format = (template: string, values: any): string =>
            stringInterpolate(template, values);

        return {
            ...componentData,
            findByKey,
            format,
        };
    }, [translations]);
};
